import React, { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ShieldCheck, Mail, ArrowLeft, CheckCircle2 } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-900 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-xl" data-testid="forgot-password-screen">
        <div className="mb-6 flex items-center gap-2">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-900">
            <ShieldCheck className="h-5 w-5 text-white" />
          </div>
          <span className="font-display text-xl font-black text-slate-900">Watch<span className="text-blue-600">Relay</span></span>
        </div>

        {sent ? (
          <div data-testid="forgot-password-sent">
            <CheckCircle2 className="h-10 w-10 text-emerald-500" />
            <h1 className="mt-3 font-display text-2xl font-black tracking-tight text-slate-900">Check your email</h1>
            <p className="mt-2 text-sm text-slate-500">
              If an account exists for <span className="font-semibold text-slate-700">{email}</span>, we've sent a link to reset your password. The link expires in 60 minutes.
            </p>
            <p className="mt-2 text-xs text-slate-400">Didn't get it? Check spam, or request another link.</p>
            <Link to="/login" className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-700">
              <ArrowLeft className="h-4 w-4" /> Back to sign in
            </Link>
          </div>
        ) : (
          <>
            <h1 className="font-display text-2xl font-black tracking-tight text-slate-900">Forgot your password?</h1>
            <p className="mt-1 text-sm text-slate-500">Enter your account email and we'll send you a secure link to reset it.</p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <Label htmlFor="fp-email" className="text-xs font-bold uppercase tracking-wider text-slate-500">Email</Label>
                <Input id="fp-email" data-testid="forgot-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1.5" autoFocus />
              </div>
              {error && <p data-testid="forgot-error" className="text-sm font-medium text-red-600">{error}</p>}
              <Button type="submit" data-testid="forgot-submit-button" disabled={busy || !email.trim()} className="w-full gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold">
                <Mail className="h-4 w-4" /> {busy ? "Sending…" : "Send reset link"}
              </Button>
            </form>
            <Link to="/login" className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-slate-400 hover:text-slate-600">
              <ArrowLeft className="h-4 w-4" /> Back to sign in
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
